import { FC, memo } from 'react';
import { useFormContext } from 'react-hook-form';

import { BoldLabelText } from '@/components/BoldLabelText/BoldLabelText';
import { DescriptionCol, DescriptionRow } from '@/components/Description';
import {
  TransactionItemDrawerMode,
  TransactionItemDrawerModeEnum,
  TransactionItemFormType,
} from '@/features/transaction_items/List/type';
import { useMinAndMaxHandlers } from '@/features/transaction_items/components/Form/Numeric/useMinAndMaxHandlers';
import { Placeholder } from '@/features/transaction_items/components/Form/Placeholder/Placeholder';
import { useTranslation } from '@/i18n';
import {
  Caption,
  FormCheckbox,
  Label,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';

import { TooltipInput } from '@/features/transaction_items/components/Form/GeneralForm/TooltipInput/TooltipInput';
import { NumericRangeInputs } from '@/features/transaction_items/components/Form/Numeric/NumericRangeInputs';
import { NumericRangeSummary } from '@/features/transaction_items/components/Form/Numeric/NumericRangeSummary';
import styles from './index.module.scss';

export type Props = {
  mode: TransactionItemDrawerMode;
};

const cx = classNames.bind(styles);

export const Numeric: FC<Props> = memo(({ mode }) => {
  const { t } = useTranslation();
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<TransactionItemFormType>();

  // Augments RHF to support dependent validations and shared error messages
  const { maxValue, minValue, onChangeMinValue, onChangeMaxValue } =
    useMinAndMaxHandlers();

  const isAllowDecimal = watch('allow_decimal');

  return (
    <>
      <DescriptionRow>
        <DescriptionCol
          label={
            <Label>
              <BoldLabelText>
                {t('transaction_items.form.numeric_range')}
              </BoldLabelText>
              {mode !== TransactionItemDrawerModeEnum.DETAIL && (
                <Caption
                  type='helper'
                  icon={false}
                  textClassName={cx(styles['range_caption_text'])}
                >
                  {t(
                    'transaction_items.drawer.component_type.numeric.range.caption'
                  )}
                </Caption>
              )}
            </Label>
          }
          labelWidth='176px'
        />
        <DescriptionCol
          value={
            <div className={cx(styles['container'])}>
              {mode === TransactionItemDrawerModeEnum.DETAIL ? (
                <NumericRangeSummary
                  minNumericValue={minValue}
                  maxNumericValue={maxValue}
                />
              ) : (
                <NumericRangeInputs
                  minValue={minValue}
                  maxValue={maxValue}
                  minValueErrors={errors?.min_value}
                  maxValueErrors={errors?.max_value}
                  onChangeMinValue={onChangeMinValue}
                  onChangeMaxValue={onChangeMaxValue}
                />
              )}
            </div>
          }
        />
      </DescriptionRow>

      <DescriptionRow>
        <DescriptionCol
          label={
            <Label>
              <BoldLabelText>
                {t(
                  'transaction_items.drawer.component_type.numeric.is_allow_decimal.label'
                )}
              </BoldLabelText>
            </Label>
          }
          labelWidth='176px'
        />
        <DescriptionCol
          value={
            <div className={cx(styles['container'])}>
              {mode === TransactionItemDrawerModeEnum.DETAIL ? (
                isAllowDecimal ? (
                  t(
                    'transaction_items.drawer.component_type.numeric.is_allow_decimal.true'
                  )
                ) : (
                  t(
                    'transaction_items.drawer.component_type.numeric.is_allow_decimal.false'
                  )
                )
              ) : (
                <FormCheckbox<TransactionItemFormType>
                  name='allow_decimal'
                  control={control}
                  inputId='allow_decimal'
                  label={t(
                    'transaction_items.drawer.component_type.numeric.is_allow_decimal.true'
                  )}
                />
              )}
            </div>
          }
        />
      </DescriptionRow>
      <TooltipInput mode={mode} />
      <Placeholder mode={mode} />
    </>
  );
});
Numeric.displayName = 'Numeric';
